import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router';
import { Plus, Calendar, Target } from 'lucide-react';
import { addSprint, updateSprint } from '../store';
import { Sprint } from '../types';
import { useApp } from '../providers/AppProvider';
import CreateSprintModal from '../components/CreateSprintModal';
import SurfaceCard from '../components/SurfaceCard';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { getSprintStatusColors, getStatusColors } from '../constants/colorScheme';


export default function SprintManagement() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { state, loading, reload } = useApp();
  const { projects, tasks, sprints } = useOrganizationData(state) as {
    projects: any[];
    tasks: any[];
    sprints: Sprint[];
  };
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<Sprint['status'] | 'all'>('all');

  if (loading || !state) {
    return <div className="p-8 text-center">Loading sprint management...</div>;
  }

  const project = projects.find((p) => p.id === projectId);
  const projectSprints = sprints.filter((s) => s.projectId === projectId);
  const filteredSprints = statusFilter === 'all' 
    ? projectSprints 
    : projectSprints.filter(s => s.status === statusFilter);

  const getFilterButtonClass = (value: 'all' | Sprint['status']) => {
    if (statusFilter !== value) {
      return 'border border-border bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground';
    }

    if (value === 'all') {
      return 'border border-primary bg-primary text-primary-foreground';
    }

    return `border ${getSprintStatusColors(value).badge}`;
  };

  const handleCreateSprint = async (sprint: Sprint) => {
    try {
      const isDebugMode = typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.search.includes('debugToasts=1'));
      if (isDebugMode) {
        const targetPath = `organizations/${sprint.organizationId}/projects/${sprint.projectId}/sprints`;
        toast.info(`Saving sprint -> ${targetPath}`, { duration: 1800 });
      }
      await addSprint(sprint);
      if (state?.currentUser?.email) {
        await reload(state.currentUser.email);
      }
      setIsCreateModalOpen(false);
      toast.success('Sprint created');
    } catch (error) {
      console.error('[SprintManagement] Failed to create sprint:', error);
      toast.error('Failed to create sprint. Please try again.');
    }
  };

  const handleSprintStatusChange = (sprintId: string, status: Sprint['status']) => {
    updateSprint(sprintId, { status });
  };

  if (!project) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <p className="mb-4 text-muted-foreground">Project not found</p>
          <Link to="/app/projects" className="text-primary hover:text-primary/80">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-3 lg:pt-4 pb-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="mb-1 text-2xl font-semibold text-foreground">Sprint Management</h1>
            <p className="text-sm text-muted-foreground">{project.name}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/app/projects/${projectId}/sprint-planning`)}
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90"
            >
              <Calendar className="w-5 h-5" />
              Planning Board
            </button>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-card-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <Plus className="w-5 h-5" />
              New Sprint
            </button>
          </div>
        </div>
      </div>

      {/* Filter by Status */}
      <SurfaceCard className="mb-6 p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">Filter:</span>
          {[
            { value: 'all', label: 'All Sprints' },
            { value: 'Planning', label: 'Planning' },
            { value: 'Active', label: 'Active' },
            { value: 'Completed', label: 'Past Sprints' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => setStatusFilter(option.value as any)}
              className={`rounded-lg px-4 py-2 text-sm transition-colors ${getFilterButtonClass(option.value as 'all' | Sprint['status'])}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </SurfaceCard>

      {/* Sprints List */}
      {filteredSprints.length === 0 ? (
        <SurfaceCard className="p-12 text-center">
          <Target className="mx-auto mb-4 h-16 w-16 text-muted-foreground/60" />
          <p className="mb-4 text-muted-foreground">No sprints yet</p>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            Create Your First Sprint
          </button>
        </SurfaceCard>
      ) : (
        <div className="space-y-6">
          {filteredSprints.map((sprint) => {
            const sprintTasks = tasks.filter((t) => sprint?.taskIds?.includes(t.id));
            const completedTasks = sprintTasks.filter((t) => t.status === 'Completed');
            const progress = sprintTasks.length > 0 ? (completedTasks.length / sprintTasks.length) * 100 : 0;
            const sprintStatusColors = getSprintStatusColors(sprint.status);
            const activeTaskColors = getStatusColors('Active');
            const inProgressTaskColors = getStatusColors('In Progress');
            const completedTaskColors = getStatusColors('Completed');
            const blockedTaskColors = getStatusColors('Blocked');

            const startDate = new Date(sprint.startDate);
            const endDate = new Date(sprint.endDate);
            const today = new Date();
            const daysRemaining = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

            return (
              <SurfaceCard key={sprint.id} className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="mb-2 text-2xl text-foreground">{sprint.name}</h2>
                    <p className="text-muted-foreground">{sprint.goals}</p>
                  </div>
                  <Badge variant="outline" className={sprintStatusColors.badge}>
                    {sprint.status}
                  </Badge>
                </div>

                {/* Sprint Dates */}
                <div className="mb-4 flex items-center gap-6 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}</span>
                  </div>
                  {sprint.status === 'Active' && (
                    <span className={daysRemaining > 0 ? 'text-primary' : 'text-destructive'}>
                      {daysRemaining > 0 ? `${daysRemaining} days remaining` : 'Sprint overdue'}
                    </span>
                  )}
                </div>

                {/* Progress */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2 text-sm">
                    <span className="text-foreground">Task Completion</span>
                    <span className="text-muted-foreground">{completedTasks.length} / {sprintTasks.length} tasks</span>
                  </div>
                  <Progress value={progress} className="h-3" />
                </div>

                {/* Sprint Actions */}
                <div className="flex flex-wrap gap-3 border-t border-border pt-4">
                  <Link
                    to={`/app/projects/${projectId}/sprints/${sprint.id}`}
                    className="rounded-lg border border-border bg-card px-4 py-2 text-card-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    View Sprint Board
                  </Link>
                  {sprint.status === 'Planning' && (
                    <button
                      onClick={() => handleSprintStatusChange(sprint.id, 'Active')}
                      className="rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90"
                    >
                      Start Sprint
                    </button>
                  )}
                  {sprint.status === 'Active' && (
                    <button
                      onClick={() => handleSprintStatusChange(sprint.id, 'Completed')}
                      className="rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90"
                    >
                      Complete Sprint
                    </button>
                  )}
                  <Link
                    to={`/app/projects/${projectId}/tasks`}
                    className="rounded-lg border border-border px-4 py-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    View Tasks
                  </Link>
                  {sprint.status === 'Completed' && (
                    <Link
                      to="/app/retrospectives"
                      className="rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90"
                    >
                      View Retrospective
                    </Link>
                  )}
                </div>

                {/* Task Summary */}
                {sprintTasks.length > 0 && (
                  <div className="mt-4 border-t border-border pt-4">
                    <h4 className="mb-3 text-sm text-foreground">Sprint Tasks</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                      <div className={`rounded-lg p-3 ${activeTaskColors.container}`}>
                        <p className={`mb-1 ${activeTaskColors.text}`}>Active</p>
                        <p className="text-2xl text-foreground">{sprintTasks.filter(t => t.status === 'Active').length}</p>
                      </div>
                      <div className={`rounded-lg p-3 ${inProgressTaskColors.container}`}>
                        <p className={`mb-1 ${inProgressTaskColors.text}`}>In Progress</p>
                        <p className="text-2xl text-foreground">{sprintTasks.filter(t => t.status === 'In Progress').length}</p>
                      </div>
                      <div className={`rounded-lg p-3 ${completedTaskColors.container}`}>
                        <p className={`mb-1 ${completedTaskColors.text}`}>Completed</p>
                        <p className="text-2xl text-foreground">{completedTasks.length}</p>
                      </div>
                      <div className={`rounded-lg p-3 ${blockedTaskColors.container}`}>
                        <p className={`mb-1 ${blockedTaskColors.text}`}>Blocked</p>
                        <p className="text-2xl text-foreground">{sprintTasks.filter(t => t.status === 'Blocked').length}</p>
                      </div>
                    </div>
                  </div>
                )}
              </SurfaceCard>
            );
          })}
        </div>
      )}

      {isCreateModalOpen && (
        <CreateSprintModal
          projectId={projectId!}
          organizationId={project.organizationId}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreateSprint}
          existingTasks={tasks.filter(t => t.projectId === projectId)}
        />
      )}
    </div>
  );
}